(() => {
  if (window.__appstleCartSubscriptionSelectorLoaded) return;
  window.__appstleCartSubscriptionSelectorLoaded = true;

  const CART_REFRESH_PARAM = '_appstle_cart_refresh';
  const SELECTOR_ATTR = '[data-appstle-cart-selector]';
  const FOCUSABLE_SELECTOR = [
    'input:not([disabled])',
    'select:not([disabled])',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const DYNAMIC_CHECKOUT_SELECTOR = '.xo-cart-mini-footer__dynamic-checkout-buttons';
  const NATIVE_APPSTLE_WIDGET_SELECTOR = '.appstle_sub_widget[id^="appstle_subscription_widget"], .appstle_sub_widget.WIDGET_TYPE_7';
  const SUBSCRIPTION_AGREEMENT_TEXT = 'One or more of the items in your cart is a deferred, subscription, or recurring purchase';
  const WALLET_STORAGE_KEY = 'camoLastCleanCartWalletMarkup';

  const state = new WeakMap();

  const dispatchCartEvents = (detail) => {
    [
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'ajaxProduct:added',
      'product:added',
      'xo:cart:refresh',
      'xo:cart:change'
    ].forEach((name) => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent(name, { detail }));
    });
  };

  const getSelectorState = (selector) => {
    if (!state.has(selector)) {
      state.set(selector, {
        timer: null,
        loading: false,
        pendingSellingPlanId: null,
        lastFocus: null
      });
    }
    return state.get(selector);
  };

  const setLoading = (selector, isLoading) => {
    selector.dataset.loading = isLoading ? 'true' : 'false';
    selector.toggleAttribute('aria-busy', isLoading);
    selector.querySelectorAll(FOCUSABLE_SELECTOR).forEach((node) => {
      node.toggleAttribute('aria-disabled', isLoading);
    });
  };

  const captureFocus = (selector) => {
    const active = document.activeElement;
    if (!active || !selector.contains(active)) return null;

    return {
      name: active.getAttribute('name') || '',
      value: active.getAttribute('value') || '',
      type: active.getAttribute('type') || '',
      selector: active.matches('select') ? 'select' : active.matches('input') ? 'input' : active.tagName.toLowerCase()
    };
  };

  const restoreFocus = (focusState) => {
    if (!focusState) return;

    const escapedName = window.CSS?.escape ? CSS.escape(focusState.name) : focusState.name.replace(/"/g, '\\"');
    const escapedValue = window.CSS?.escape ? CSS.escape(focusState.value) : focusState.value.replace(/"/g, '\\"');
    const candidates = [
      `${focusState.selector}[name="${escapedName}"][value="${escapedValue}"]`,
      `${focusState.selector}[name="${escapedName}"]`,
      SELECTOR_ATTR + ' ' + FOCUSABLE_SELECTOR
    ];

    for (const candidate of candidates) {
      const node = document.querySelector(candidate);
      if (node && typeof node.focus === 'function') {
        node.focus({ preventScroll: true });
        break;
      }
    }
  };

  const replaceNodeContents = (selector, sourceDoc, options = {}) => {
    const currentNodes = document.querySelectorAll(selector);
    if (!currentNodes.length || !sourceDoc) return false;

    let replaced = false;
    currentNodes.forEach((currentNode) => {
      const uniqueId = currentNode.getAttribute('xo-unique-id');
      if (options.skipUniqueIds?.includes(uniqueId)) return;

      const sourceSelector = uniqueId ? `${selector}[xo-unique-id="${uniqueId}"]` : selector;
      const freshNode = sourceDoc.querySelector(sourceSelector);
      if (!freshNode) return;

      currentNode.innerHTML = freshNode.innerHTML;
      replaced = true;
    });

    return replaced;
  };

  const replaceFirstNodeContents = (selector, sourceDoc) => {
    const currentNode = document.querySelector(selector);
    const freshNode = sourceDoc?.querySelector(selector);
    if (!currentNode || !freshNode) return false;

    currentNode.innerHTML = freshNode.innerHTML;
    return true;
  };

  const nodeHasSubscriptionAgreement = (node) => {
    return Boolean(node && node.textContent && node.textContent.includes(SUBSCRIPTION_AGREEMENT_TEXT));
  };

  const findSubscriptionAgreementNode = (root = document) => {
    const container = root.querySelector?.(DYNAMIC_CHECKOUT_SELECTOR);
    if (!container) return null;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let textNode;
    while ((textNode = walker.nextNode())) {
      if (textNode.nodeValue?.includes(SUBSCRIPTION_AGREEMENT_TEXT)) {
        return textNode.parentElement;
      }
    }
    return null;
  };

  const hideSubscriptionAgreementMessage = (root = document) => {
    const agreementNode = findSubscriptionAgreementNode(root);
    if (!agreementNode) return;

    const walletContainer = agreementNode.closest(DYNAMIC_CHECKOUT_SELECTOR);
    if (!walletContainer) return;

    let nodeToHide = agreementNode;
    while (nodeToHide.parentElement && nodeToHide.parentElement !== walletContainer) {
      const parentText = nodeToHide.parentElement.textContent || '';
      if (parentText.includes(SUBSCRIPTION_AGREEMENT_TEXT)) {
        nodeToHide = nodeToHide.parentElement;
      } else {
        break;
      }
    }

    nodeToHide.setAttribute('aria-hidden', 'true');
    nodeToHide.style.setProperty('position', 'absolute', 'important');
    nodeToHide.style.setProperty('width', '1px', 'important');
    nodeToHide.style.setProperty('height', '1px', 'important');
    nodeToHide.style.setProperty('overflow', 'hidden', 'important');
    nodeToHide.style.setProperty('clip-path', 'inset(50%)', 'important');
    nodeToHide.style.setProperty('white-space', 'nowrap', 'important');
    nodeToHide.style.setProperty('margin', '0', 'important');
    nodeToHide.style.setProperty('padding', '0', 'important');
  };

  const isCleanDynamicCheckoutMarkup = (node) => {
    return Boolean(node && !nodeHasSubscriptionAgreement(node) && node.querySelector('shopify-accelerated-checkout-cart, shopify-accelerated-checkout, [data-testid], button, iframe'));
  };

  const rememberDynamicCheckoutMarkup = () => {
    const node = document.querySelector(DYNAMIC_CHECKOUT_SELECTOR);
    if (!isCleanDynamicCheckoutMarkup(node)) return '';

    const markup = node.outerHTML;
    try {
      window.sessionStorage?.setItem(WALLET_STORAGE_KEY, markup);
    } catch (error) {
      // Storage can be unavailable in some privacy modes. In-memory preservation still works.
    }
    return markup;
  };

  const getPreservedDynamicCheckoutMarkup = () => {
    const freshMarkup = rememberDynamicCheckoutMarkup();
    if (freshMarkup) return freshMarkup;

    try {
      return window.sessionStorage?.getItem(WALLET_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  const restorePreservedDynamicCheckoutMarkup = (markup = getPreservedDynamicCheckoutMarkup()) => {
    const currentNode = document.querySelector(DYNAMIC_CHECKOUT_SELECTOR);
    if (!currentNode) return false;

    if (!nodeHasSubscriptionAgreement(currentNode)) {
      rememberDynamicCheckoutMarkup();
      return false;
    }

    if (!markup) {
      hideSubscriptionAgreementMessage();
      return false;
    }

    const template = document.createElement('template');
    template.innerHTML = markup.trim();
    const preservedNode = template.content.firstElementChild;

    if (!preservedNode) {
      hideSubscriptionAgreementMessage();
      return false;
    }

    currentNode.replaceWith(preservedNode);
    return true;
  };

  const hideNativeAppstleProductWidgets = (root = document) => {
    root.querySelectorAll?.(NATIVE_APPSTLE_WIDGET_SELECTOR).forEach((widget) => {
      if (widget.closest('.xo-cart-mini, .xo-cart-drawer, .xo-modal, .appstle-cart-subscription-tab, [data-appstle-cart-selector]')) return;
      widget.setAttribute('data-camo-hidden-native-appstle-widget', 'true');
      widget.style.setProperty('display', 'none', 'important');
    });
  };

  const stabilizeDynamicCheckoutButtons = () => {
    const node = document.querySelector(DYNAMIC_CHECKOUT_SELECTOR);
    if (!node) return;

    if (nodeHasSubscriptionAgreement(node)) {
      restorePreservedDynamicCheckoutMarkup();
      hideSubscriptionAgreementMessage();
    } else {
      rememberDynamicCheckoutMarkup();
    }
  };

  const refreshCartDrawerMarkup = async (detail, focusState, options = {}) => {
    dispatchCartEvents(detail);

    const preservedDynamicCheckoutMarkup = options.preserveDynamicCheckoutButtons
      ? getPreservedDynamicCheckoutMarkup()
      : '';

    const url = new URL(window.location.href);
    url.searchParams.set(CART_REFRESH_PARAM, Date.now().toString());

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) return false;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    let changed = false;

    if (options.keepSubscriptionTabOpen) {
      changed = replaceNodeContents('xo-cart-will-change', doc, {
        skipUniqueIds: ['xo-cart-mini-drawer-footer']
      }) || changed;
      changed = replaceFirstNodeContents('.xo-cart-mini-footer', doc) || changed;
      restorePreservedDynamicCheckoutMarkup(preservedDynamicCheckoutMarkup);
    } else {
      changed = replaceNodeContents('xo-cart-will-change', doc) || changed;
      restorePreservedDynamicCheckoutMarkup(preservedDynamicCheckoutMarkup);
    }

    changed = replaceNodeContents('xo-cart-size', doc) || changed;
    changed = replaceNodeContents('[data-cart-count]', doc) || changed;
    changed = replaceNodeContents('[data-header-cart-count]', doc) || changed;

    dispatchCartEvents(detail);
    requestAnimationFrame(() => restoreFocus(focusState));

    return changed;
  };

  const changeCartLine = async ({ line, quantity, sellingPlanId }) => {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
        selling_plan: sellingPlanId || ''
      })
    });

    const cart = await response.json();
    if (!response.ok) throw cart;
    return cart;
  };

  const getLinePayloads = (selector) => {
    const lineNodes = [...selector.querySelectorAll('[data-appstle-cart-line]')];

    if (lineNodes.length) {
      return lineNodes
        .map((node) => ({
          line: node.dataset.line,
          quantity: node.dataset.quantity
        }))
        .filter((lineData) => lineData.line && lineData.quantity)
        .reverse();
    }

    return [{
      line: selector.dataset.line,
      quantity: selector.dataset.quantity
    }].filter((lineData) => lineData.line && lineData.quantity);
  };

  const runUpdate = async (selector, sellingPlanId) => {
    const selectorState = getSelectorState(selector);

    if (selectorState.loading) {
      selectorState.pendingSellingPlanId = sellingPlanId;
      return;
    }

    selectorState.loading = true;
    selectorState.pendingSellingPlanId = null;
    selectorState.lastFocus = captureFocus(selector);
    setLoading(selector, true);

    try {
      let cart;
      const lines = getLinePayloads(selector);

      for (const lineData of lines) {
        cart = await changeCartLine({ ...lineData, sellingPlanId });
      }

      await refreshCartDrawerMarkup(
        { cart, selling_plan: sellingPlanId || null },
        selectorState.lastFocus,
        {
          keepSubscriptionTabOpen: Boolean(selector.closest('.appstle-cart-subscription-tab')),
          preserveDynamicCheckoutButtons: Boolean(selector.closest('.appstle-cart-subscription-tab'))
        }
      );
    } catch (error) {
      console.error('Unable to update subscription option through AJAX', error);
      dispatchCartEvents({ error, selling_plan: sellingPlanId || null });
    } finally {
      selectorState.loading = false;
      setLoading(selector, false);

      if (selectorState.pendingSellingPlanId !== null) {
        const nextSellingPlanId = selectorState.pendingSellingPlanId;
        selectorState.pendingSellingPlanId = null;
        runUpdate(selector, nextSellingPlanId);
      }
    }
  };

  const updateSelector = (selector, sellingPlanId) => {
    if (!selector) return;

    const selectorState = getSelectorState(selector);
    window.clearTimeout(selectorState.timer);
    selectorState.pendingSellingPlanId = sellingPlanId;

    selectorState.timer = window.setTimeout(() => {
      const nextSellingPlanId = selectorState.pendingSellingPlanId;
      selectorState.pendingSellingPlanId = null;
      runUpdate(selector, nextSellingPlanId);
    }, 120);
  };

  const syncSelectedOption = (selector, input) => {
    if (!selector || !input) return;

    selector.querySelectorAll('.appstle-cart-selector__option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(input));
    });

    selector.querySelectorAll('.appstle-cart-selector__frequency').forEach((frequency) => {
      frequency.classList.add('is-hidden');
    });
  };

  const syncSelectedFrequency = (input) => {
    const frequency = input.closest('.appstle-cart-selector__frequency');
    frequency?.querySelectorAll('.appstle-cart-selector__frequency-option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(input));
    });
  };

  document.addEventListener('submit', (event) => {
    if (!event.target.closest(SELECTOR_ATTR)) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  document.addEventListener('click', (event) => {
    const submitter = event.target.closest('button[type="submit"], input[type="submit"]');
    if (!submitter || !submitter.closest(SELECTOR_ATTR)) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  document.addEventListener('change', (event) => {
    const selector = event.target.closest(SELECTOR_ATTR);
    if (!selector) return;

    if (event.target.matches('.appstle-cart-selector__frequency-option input[type="radio"]')) {
      event.preventDefault();
      syncSelectedFrequency(event.target);
      updateSelector(selector, event.target.value);
      return;
    }

    if (event.target.matches('input[type="radio"]')) {
      event.preventDefault();
      syncSelectedOption(selector, event.target);

      const nextFrequency = event.target.closest('.appstle-cart-selector__option')?.nextElementSibling;
      if (nextFrequency?.classList.contains('appstle-cart-selector__frequency')) {
        nextFrequency.classList.remove('is-hidden');
        const checkedFrequency = nextFrequency.querySelector('.appstle-cart-selector__frequency-option input:checked');
        const firstFrequency = nextFrequency.querySelector('.appstle-cart-selector__frequency-option input');
        updateSelector(selector, checkedFrequency ? checkedFrequency.value : (firstFrequency ? firstFrequency.value : event.target.value));
        return;
      }

      updateSelector(selector, event.target.value);
      return;
    }

    if (event.target.matches('select.appstle-cart-selector__select')) {
      event.preventDefault();
      updateSelector(selector, event.target.value);
    }
  }, true);

  const bootStabilityLayer = () => {
    hideNativeAppstleProductWidgets();
    stabilizeDynamicCheckoutButtons();

    const observer = new MutationObserver((mutations) => {
      let shouldStabilizeWallets = false;
      let shouldHideNativeWidgets = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.matches?.(DYNAMIC_CHECKOUT_SELECTOR) || node.querySelector?.(DYNAMIC_CHECKOUT_SELECTOR)) {
            shouldStabilizeWallets = true;
          }

          if (node.matches?.(NATIVE_APPSTLE_WIDGET_SELECTOR) || node.querySelector?.(NATIVE_APPSTLE_WIDGET_SELECTOR)) {
            shouldHideNativeWidgets = true;
          }
        });
      });

      if (shouldHideNativeWidgets) hideNativeAppstleProductWidgets();
      if (shouldStabilizeWallets) requestAnimationFrame(stabilizeDynamicCheckoutButtons);
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootStabilityLayer, { once: true });
  } else {
    bootStabilityLayer();
  }
})();
